import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Upload,
  Save,
  ArrowLeft,
  Loader2,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";
import {
  AddBannersThunk,
  GetSingleBannersThunk,
  UpdateBannersThunk,
} from "../../redux/states/banners/thunk";

const CreateUpdateBanners = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleBanner, loading, error } = useSelector(
    (state) => state.banners
  );

  const [formData, setFormData] = useState({
    title: "",
    sub_title: "",
    tagline: "",
    sort_description: "",
    description: "",
    button: "",
    button_link: "",
    position: 1,
    status: "active",
    type: "hero_section",
    image: null,
  });

  const [preview, setPreview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // ✅ Load banner when updating
  useEffect(() => {
    if (uuid) {
      setIsUpdating(true);
      dispatch(GetSingleBannersThunk(uuid));
    }
  }, [uuid, dispatch]);

  // ✅ Prefill form when banner data is fetched
  useEffect(() => {
    if (singleBanner && isUpdating) {
      setFormData({
        title: singleBanner.title || "",
        sub_title: singleBanner.sub_title || "",
        tagline: singleBanner.tagline || "",
        sort_description: singleBanner.sort_description || "",
        description: singleBanner.description || "",
        button: singleBanner.button || "",
        button_link: singleBanner.button_link || "",
        position: singleBanner.position || 1,
        status: singleBanner.status || "active",
        type: singleBanner.type || "hero_section",
        image: null,
      });
      if (singleBanner.image) {
        setPreview(`${process.env.REACT_APP_API_URL}/${singleBanner.image}`);
      }
    }
  }, [singleBanner, isUpdating]);

  // ✅ Handle input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setFormData({ ...formData, [name]: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // ✅ Remove selected image
  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setPreview(null);
  };

  // ✅ Submit form
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert("Please enter the banner title");
      return;
    }

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) data.append(key, value);
    });
    if (isUpdating) data.append("uuid", uuid);

    if (isUpdating) {
      dispatch(UpdateBannersThunk({ uuid, formData: data })).then((res) => {
        if (res.type === "banners/UpdateBannersThunk/fulfilled") {
          navigate("/banners");
        }
      });
    } else {
      dispatch(AddBannersThunk(data)).then((res) => {
        if (res.type === "banners/AddBannersThunk/fulfilled") {
          navigate("/banners");
        }
      });
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  {isUpdating ? "Update Banner" : "Add New Banner"}
                </h1>
                <p className="text-gray-600 mt-2">
                  {isUpdating
                    ? "Edit the banner details below."
                    : "Fill the form to create a new banner."}
                </p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 border border-gray-300 px-4 py-2 rounded-lg transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-red-700">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Details */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Banner Details
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  ["title", "Title *"],
                  ["sub_title", "Sub Title"],
                  ["tagline", "Tagline"],
                  ["sort_description", "Short Description"],
                  ["description", "Description"],
                  ["button", "Button Text"],
                  ["button_link", "Button Link"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block font-medium text-gray-700 mb-2">
                      {label}
                    </label>
                    <input
                      type="text"
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}

                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Position
                  </label>
                  <input
                    type="number"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Banner Image
              </h2>
              <div className="flex flex-col items-center space-y-4">
                {preview ? (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-64 h-40 object-cover rounded-xl border shadow"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-64 h-40 flex flex-col items-center justify-center border-2 border-dashed rounded-xl text-gray-400">
                    <ImageIcon className="w-10 h-10 mb-2" />
                    No image selected
                  </div>
                )}

                <label className="flex items-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-xl cursor-pointer hover:bg-blue-700 transition">
                  <Upload className="w-5 h-5" />
                  {preview ? "Change Image" : "Upload Image"}
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isUpdating ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isUpdating ? "Update Banner" : "Save Banner"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateBanners;
